/**
 * @deprecated This component is being replaced by the unified CDS-based
 * `SearchBarContentWrapper` (search-common) via the Workstream 2 autocomplete
 * migration. Scheduled for deletion in https://coursera.atlassian.net/browse/SEARCH-2179 after the adapter swap lands.
 */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent as AutosuggestChangeEvent, OnSuggestionSelected } from 'react-autosuggest';
import Autosuggest from 'react-autosuggest';
import Media from 'react-media';

import classNames from 'classnames';
import URI from 'jsuri';

import Imgix from 'js/components/Imgix';
import type { TrackingData } from 'js/lib/retracked';
import { useRetracked } from 'js/lib/retracked';
import useRouter from 'js/lib/useRouter';

import { IconButton, Typography2 } from '@coursera/cds-core';
import { CloseIcon, SearchIcon, VideoIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import { useProgramSkillData } from 'bundles/enterprise-learner-search/components/skillSearch/SkillSearchDataProvider';
import { useSkillSetData } from 'bundles/enterprise-learner-search/components/skillsetSearch/SkillSetSearchDataProvider';
import {
  PAGE_HEADER_MOBILE_BREAKPOINT,
  SHORT_FORM_CONTENT_VIDEO_ENTITY_TYPE,
} from 'bundles/enterprise-learner-search/constants';
import { getEnterpriseAutoCompleteSections } from 'bundles/enterprise-learner-search/utils/autocomplete';
import { getEnterpriseSearchConfigs } from 'bundles/enterprise-learner-search/utils/search';
import { createEnterpriseSearchExperimentImpression } from 'bundles/enterprise-legacy-learner-home/utils/enterpriseSearchExperiment';
import getDurationFormatted from 'bundles/enterprise-legacy-learner-home/utils/getDurationFormatted';
import { launchMiniModal } from 'bundles/enterprise-legacy-xdp/hocs/withMiniModal';
import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import AppInfoContext from 'bundles/page/contexts/AppInfoContext';
import SvgCompatibleImage from 'bundles/search-common/components/SvgCompatibleImage';
import {
  SEARCH_ENTERPRISE_AUTOCOMPLETE_CLIPS_INDEX_ID,
  SEARCH_ENTERPRISE_AUTOCOMPLETE_COURSES_S12N_INDEX_ID,
} from 'bundles/search-common/constants';
import { AUTOCOMPLETE_PHOTO_SIZE, DEBOUNCE_DURATION_MS } from 'bundles/search-common/constants/autocomplete';
import useSearchBarShortcut from 'bundles/search-common/hooks/useSearchBarShortcut';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';
import type { AutocompleteSection } from 'bundles/search-common/types/autocomplete';
import { getDataToTrackFromHit } from 'bundles/search-common/utils/SearchUtils';
import { generateSkillsPageUrl } from 'bundles/skills-common/private/utils/skillLinkUtils';

import CourseraLogo from 'images/page-header/coursera-logo-c-mark.png';

import _t from 'i18n!nls/enterprise-learner-search';

import 'css!./__styles__/EnterpriseAutoComplete';

/**
 * This autocomplete component is a controlled one: `refine` is a function taking in a user
 * We are using `react-autosuggest`: https://github.com/moroshko/react-autosuggest
 * query and will return `hits` that Algolia thinks are good autocomplete suggestions.
 */

type Props = {
  pathname: string;
  placeholder: string;
  programIds: string[];
  /* This prop with a A/B test is used to determine if we should show skill pages in auto complete results */
  enableSkillsInSearchAndBrowse?: boolean;
  shouldShowShortFormContent?: boolean;
  shouldRenderScopeSelect?: boolean;
  forceFullWidthMobile?: boolean;
};

type Hit = {
  name: string;
  objectID: string;
  imageUrl?: string;
  partners?: Array<string>;
  topSkill?: string;
  trackingName?: string;
  trackingData?: TrackingData;
  entityType?: string;
  durationMs?: number;
};

// AutoSuggest types don't correctly handle grouped suggestions, so yolo it with any.
type SuggestionType = $TSFixMe;

const styles = {
  autoComplete: css`
    input[type='text'].react-autosuggest__input {
      :not(:focus) {
        position: absolute;
        top: -100%;
        left: -100%;
      }

      :focus {
        position: relative;
        top: unset;
        left: unset;
      }
    }

    .react-autosuggest__container {
      width: auto;
    }

    .react-autosuggest__suggestions-container--open,
    .suggestion-wrapper.withSuggestion .react-autosuggest__suggestions-container {
      /* page-header and program-home bundles have different breakpoints, so we have to duplicate 
         the styles from EnterpriseAutoComplete.styl, to make sure search is available for viewports b/w 993px and 1060px */
      position: absolute;
      left: 0;
      top: 64px;
      width: 100% !important;
      max-width: unset;
      max-height: unset;
      padding: 0;
      z-index: 1;
      background-color: var(--cds-color-neutral-background-primary);
      height: calc(100vh - 64px);
      overflow-x: hidden;
      overflow-y: auto;
      margin-top: 0;
    }

    .cancel-button {
      display: block;
    }
  `,
  autoCompleteIsFocused: css`
    /* page-header and program-home bundles have different breakpoints, so we have to duplicate 
       the styles from EnterpriseAutoComplete.styl, to make sure search is available for viewports b/w 993px and 1060px */
    padding: 12px 14px;
    background-color: var(--cds-color-emphasis-primary-background-xxstrong);
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    z-index: 4000;

    input[type='text'].react-autosuggest__input {
      padding-right: 30px;
    }

    .react-autosuggest__container {
      width: 100%;
    }
  `,
  autoCompleteSearchIcon: (isFocused: boolean) => css`
    display: ${isFocused ? 'block' : 'none'} !important;
  `,
  mobileSearchIcon: css`
    && {
      width: 36px;
      height: 36px;
      color: var(--cds-color-neutral-primary-weak);

      .isFocused & {
        display: none;
      }

      :focus,
      :hover {
        background: transparent;
      }
    }
  `,
  preUnifiedPHSearchBar: css`
    /* As per PageHeader.styl, mobile header kicks in at 1060px; these styles are for desktop only */
    @media (min-width: 1061px) {
      .react-autosuggest__container > input {
        border-radius: 24px !important;
        padding: var(--cds-spacing-100) var(--cds-spacing-600) !important;
        border: 1px solid var(--cds-color-grey-50) !important;
        color: var(--cds-color-grey-700) !important;
        line-height: 24px !important;
        font-family: 'Open Sans', sans-serif !important;
        min-width: 265px;
        height: 44px !important;
      }

      .search-button {
        position: relative;
        right: 41px;
        min-width: 32px;
        display: inline-grid;
        border-radius: 24px;
        width: 32px;
        height: 32px;
        padding: var(--cds-spacing-100);
        top: 6px;
        border: 1px solid transparent;
      }
    }
  `,
  preUnifiedPHSearchBarCourseraIcon: css`
    @media (min-width: 1061px) {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      margin-left: 7px;
    }
  `,
  forcedDesktopMode: css`
    /* Make the whole autocomplete container full width */
    width: 100%;
    display: flex;
    align-items: center;
    position: relative;

    /*  Input styling (same as desktop, but no media query)  */
    .react-autosuggest__container > input {
      border-radius: var(--cds-spacing-300) !important;
      padding: var(--cds-spacing-100) var(--cds-spacing-600) !important;
      border: 1px solid var(--cds-color-grey-50) !important;
      color: var(--cds-color-grey-700) !important;
      line-height: var(--cds-spacing-300) !important;
      font-family: 'Open Sans', sans-serif !important;
      min-width: 265px;
      height: 44px !important;
    }

    /* Ensure autosuggest container expands */
    .react-autosuggest__container {
      width: 100% !important;
      flex: 1;
    }

    /* Search button (desktop style) */
    .search-button {
      position: relative;
      right: 41px;
      min-width: var(--cds-spacing-400);
      display: inline-grid;
      border-radius: var(--cds-spacing-300);
      width: var(--cds-spacing-400);
      height: var(--cds-spacing-400);
      padding: var(--cds-spacing-100);
      border: 1px solid transparent;
    }

    /*  Coursera logo positioning (desktop style) */
    .coursera-logo-enterprise-search {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      margin-left: 7px;
    }

    .close-icon-enterprise-search-icon {
      color: var(--cds-color-white-0);
    }
  `,
};

export const EnterpriseAutoComplete = (props: Props) => {
  const {
    pathname,
    placeholder,
    enableSkillsInSearchAndBrowse,
    shouldShowShortFormContent,
    shouldRenderScopeSelect,
    programIds,
    forceFullWidthMobile,
  } = props;

  const inputRef = useRef<HTMLInputElement | undefined>(undefined);
  const searchButtonRef = useRef<HTMLButtonElement | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();
  useSearchBarShortcut(inputRef);
  const router = useRouter();
  const query = router.location.query.query || '';
  const trackComponent = useRetracked();

  const track = useTracker();
  const [isFocused, setIsFocused] = useState(false);
  const [currentText, setCurrentText] = useState(query || '');
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [hiddenEmptySearchClipsObjectIds, setHiddenEmptySearchClipsObjectIds] = useState<string[]>([]);
  const { getSearchResults, updateQuery, isLoading } = useSearch();
  const clipsData = shouldShowShortFormContent
    ? getSearchResults(SEARCH_ENTERPRISE_AUTOCOMPLETE_CLIPS_INDEX_ID)
    : undefined;
  const { elements: clipElements, source: clipsSource } = clipsData || {};
  const indexName = clipsSource?.indexName;
  const coursesS12nData = getSearchResults(SEARCH_ENTERPRISE_AUTOCOMPLETE_COURSES_S12N_INDEX_ID);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<AutocompleteSection[]>([]);
  const { skillsetHits = [] } = useSkillSetData(currentText, programIds);
  const { skillHits = [] } = useProgramSkillData(currentText, programIds);
  const refineAndTrack = useCallback<(newValue: string) => void>(
    (newValue: string) => {
      const whitespacelessSearchValue = newValue.replace(/\s+/g, ' '); // white space collapsed
      trackComponent({
        trackingData: { newSearchValue: whitespacelessSearchValue, page: window.location.pathname },
        trackingName: 'algolia_search_input_changes',
        action: 'search',
      });

      updateQuery(newValue);
    },
    [trackComponent, updateQuery]
  );

  const debouncedRefineAndTrack = useCallback<(newQuery: string) => void>(
    (newQuery: string) => {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = setTimeout(() => {
        refineAndTrack(newQuery);
      }, DEBOUNCE_DURATION_MS);
    },
    [refineAndTrack]
  );

  const memoizedEnterpriseAutoCompleteSections = useMemo(() => {
    return getEnterpriseAutoCompleteSections(clipsData, coursesS12nData);
  }, [clipsData, coursesS12nData]);

  useEffect(() => {
    if (query && typeof window !== 'undefined' && query !== 'undefined') {
      debouncedRefineAndTrack(query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // optimization for reducing calculation when loading
    if (!isLoading) {
      setAutocompleteSuggestions(memoizedEnterpriseAutoCompleteSections);
    }
  }, [isLoading, memoizedEnterpriseAutoCompleteSections]);

  const isInEnterpriseSearch = () => {
    const {
      params: { programSlug },
      location: { pathname: routerPathname },
    } = router;

    return routerPathname.includes(`/programs/${programSlug}/browse`);
  };

  const hideEmptySearchClips = (renderHits: SearchProductHit[]) => {
    const hiddenObjectIds: string[] = (renderHits || [])
      .filter((hit) => hit?.productType === SHORT_FORM_CONTENT_VIDEO_ENTITY_TYPE && hit?.id !== undefined)
      .map((hit) => hit?.id as string);

    setHiddenEmptySearchClipsObjectIds(hiddenObjectIds);
  };

  const constructHardRedirectURI = (path: string) => {
    // We are intentionally removing this from filteredQuery to remove them from the query

    const { source, currentTab, showMiniModal, productId, productType, collectionId, ...filteredQuery } =
      router.location.query;
    const isEnterpriseSERP = source === 'search';
    const uri = new URI(path);

    if (!isEnterpriseSERP && filteredQuery) {
      Object.keys(filteredQuery).forEach((key) => {
        uri.addQueryParam(key, filteredQuery[key]);
      });
    }

    uri.replaceQueryParam('source', 'search').replaceQueryParam('query', currentText);

    return uri.toString();
  };

  const doSearch = () => {
    // make sure that the modal is not displayed after going to a search page
    // We are intentionally removing this from filteredQuery to remove them from the query

    const { source, currentTab, showMiniModal, productId, productType, collectionId, ...filteredQuery } =
      router.location.query;
    trackComponent({
      trackingData: { searchText: currentText, page: window.location.pathname },
      trackingName: 'algolia_search_page_query_updated',
      action: 'search',
    });

    track('perform_search', {
      searchedQuery: currentText,
    });

    if (isInEnterpriseSearch()) {
      const isEnterpriseSERP = source === 'search';
      const routerConfig = {
        pathname: `${pathname}/browse`,
        query: {
          query: currentText,
          source: 'search',
        },
      };

      if (!isEnterpriseSERP) {
        routerConfig.query = {
          ...filteredQuery,
          ...routerConfig.query,
        };
      }

      router.push(routerConfig);

      // Unified pages can use this search bar too, ensure we hard redirect for these pages.
    } else {
      window.location.assign(constructHardRedirectURI(`${pathname}/browse`));
    }
  };

  const onAutoSuggestChange = (event: React.FormEvent, { newValue, method }: AutosuggestChangeEvent) => {
    // When there's an input change on AutoComplete, we update with new value
    // if the user typed or clicked, we call refine (from Algolia) to update the
    // AutoComplete suggestions
    setCurrentText(newValue || '');
    if ((method === 'type' || method === 'click') && newValue !== currentText) {
      debouncedRefineAndTrack(newValue);
    }
  };

  const onSuggestionSelected: OnSuggestionSelected<SuggestionType> = (event, { suggestion, method }) => {
    // If selected using enter key, we set this to true to avoid operation on onKeyDown, where we will set it back to false
    if (method === 'enter') {
      setSuggestionSelected(true);
    }

    track('perform_search', {
      searchedQuery: suggestion.name,
      sourceLinkCategory: 'autocomplete_suggestions',
    });
    trackComponent({
      trackingData: {
        searchText: currentText,
        page: window.location.pathname,
        ...getDataToTrackFromHit(suggestion),
        hitType: suggestion.trackingName,
        ...(suggestion.trackingData ?? {}),
      },
      trackingName: suggestion.trackingName,
      action: 'search',
    });

    const suggestionProductId: string[] | undefined = suggestion?.objectID?.split('~');
    if (suggestionProductId && suggestionProductId.length >= 2) {
      const [suggestionType, suggestionID] = suggestionProductId;
      if (suggestionType === 'skillset') {
        // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        const [programSlug, skillsetId] = suggestionID.split('/');
        const nextPath = `/programs/${programSlug}/skillsets/${skillsetId}`;

        window.location.assign(nextPath);
        return;
      }

      const queryText = encodeURIComponent(currentText);

      if (suggestionType === 'skill') {
        const { programSlug } = router.params;
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        const nextPath = `${generateSkillsPageUrl(suggestionID, programSlug)}?query=${queryText}&source=search`;

        window.location.assign(nextPath);
        return;
      }

      // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (['sfcvideo', 'sfclesson'].includes(suggestionType)) {
        window.open(`${suggestion.objectUrl}?query=${queryText}&source=search`, '_blank');
        return;
      }

      // Object URL's are in this format "/learn/slug". If value is present, index 1 is product type path and index 2 is slug.
      const splitObjectUrl = suggestion?.objectUrl?.split('/');
      // If we're doing a cross-scope auto-complete, then also redirect to the correct context. This avoids us having to
      // make every page multi-program mini-modal aware just yet.
      const shouldRedirect = !router.location.pathname.startsWith(pathname);
      if (shouldRedirect) {
        launchMiniModal(
          router,
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          suggestionID,
          suggestionType === 'course',
          '',
          pathname,
          { query: currentText },
          splitObjectUrl && { slug: splitObjectUrl[2], productPagePath: splitObjectUrl[1] }
        );
      } else {
        launchMiniModal(
          router,
          // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          suggestionID,
          suggestionType === 'course',
          '',
          undefined,
          undefined,
          splitObjectUrl && { slug: splitObjectUrl[2], productPagePath: splitObjectUrl[1] }
        );
      }
    }
  };

  const blurAutocomplete = () => {
    if (inputRef) {
      inputRef.current?.blur();
    }
  };

  const onBlur = (tabIndex?: string) => () => {
    if (tabIndex) {
      inputRef?.current?.setAttribute('tabIndex', tabIndex);
    }
    setIsFocused(false);
  };

  const onFocus = () => {
    trackComponent({
      trackingData: { page: window.location.pathname },
      trackingName: 'autocomplete_focused',
      action: 'search',
    });

    setIsFocused(true);

    // Checks that the component is mounted and search hits have been fetched, then makes sure the Clips that are
    // returned with an empty search are hidden by adding their objectIds to the 'hiddenEmptySearchClipsObjectIds'
    // state
    if (clipElements && !currentText && !hiddenEmptySearchClipsObjectIds.length) {
      hideEmptySearchClips(clipElements as SearchProductHit[]);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      // This is done so that the container is closed when pressing escape
      blurAutocomplete();
    }
    // Without the second check this logic would also happen if the user selected a suggestion via the keyboard, so we set this.suggestionSelected
    // to true on onSuggestionSelected
    else if (event.key === 'Enter' && !suggestionSelected) {
      doSearch();
      blurAutocomplete();
    }
    // Makes sure the focus goes to the expected element since it is not working as expected in firefox
    else if (event.key === 'Tab') {
      if (!event.shiftKey) {
        searchButtonRef?.current?.focus();
      } else {
        const enterpriseLogo = document.querySelector('a.c-logo');
        if (enterpriseLogo instanceof HTMLAnchorElement) {
          enterpriseLogo.focus();
        }
      }
    }
    // We now set it back to false to indicate that the selection has been finalized & if the user presses a key again it won't be
    // interpreted as selected from the menu unless they select from the menu, which will cause onSuggestionSelected is fired again and set it
    // back to true
    setSuggestionSelected(false);
  };

  const clearSearchQuery = () => {
    setCurrentText('');
  };

  const storeInputReference = (instance: Autosuggest<SuggestionType> | null) => {
    if (instance?.input) {
      inputRef.current = instance.input;
    }
  };

  const searchButtonOnClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    doSearch();
    blurAutocomplete();
  };

  const clearSearchQueryAndCloseContainer = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    clearSearchQuery();
    blurAutocomplete();
  };

  const renderAutoCompleteSuggestion = (hit: Hit | SuggestionType) => {
    const searchIndexName = indexName;
    const e2eTestLabel = hit.objectID ? `${hit.objectID?.split('~')[0]}-autocomplete-result` : undefined;

    let svgIcon;

    if (hit?.entityType === SHORT_FORM_CONTENT_VIDEO_ENTITY_TYPE) {
      svgIcon = <VideoIcon size="medium" color="interactive" />;
    }

    return (
      <TrackedDiv
        key={hit.objectID}
        trackingName={hit.trackingName ?? 'autocomplete_item'}
        data={{
          name: hit.name,
          objectID: hit.objectID,
          indexName: searchIndexName,
          ...(hit.trackingData ?? {}),
        }}
        withVisibilityTracking={true}
        className="section-item direct-match-section"
        aria-label={`${hit.sectionTitle}: ${hit.name}`}
        data-e2e={e2eTestLabel}
      >
        {svgIcon ? (
          <div className="svg-icon">{svgIcon}</div>
        ) : (
          <SvgCompatibleImage
            className="item-photo"
            src={hit.imageUrl}
            alt={_t('Suggestion Image')}
            imgParams={{ fit: 'crop', auto: 'format,compress' }}
            width={AUTOCOMPLETE_PHOTO_SIZE}
            height={AUTOCOMPLETE_PHOTO_SIZE}
          />
        )}
        <div className="item-label">
          <Typography2 className="item-name" title={hit.name} component="span">
            <span className="sr-only">{hit.sectionTitle}:&nbsp;</span>
            {hit.name}
          </Typography2>
          {hit.partners?.[0] && (
            <Typography2 color="supportText" component="span">
              {hit.partners[0]}
              {hit?.durationMs && (
                <Typography2 color="supportText" component="span">
                  {' · ' + getDurationFormatted(hit.durationMs || 0, true)}
                </Typography2>
              )}
            </Typography2>
          )}

          {hit.topSkill && (
            <Typography2 className="partner-name" color="supportText" component="span">
              {_t('Learn #{skillName} and other skills...', { skillName: hit.topSkill })}
            </Typography2>
          )}
        </div>
      </TrackedDiv>
    );
  };

  const renderSectionTitle = ({ sectionTitle, hits: sectionHits }: { sectionTitle: string; hits: Hit[] }) => {
    if (!sectionTitle || !sectionHits.length) {
      return null;
    }

    return (
      <div className="section-title-box">
        <Typography2 className="section-title" variant="subtitleMedium" component="span">
          {sectionTitle}
        </Typography2>
      </div>
    );
  };

  // we are using onChange rather than the following 2 props since it's a more common pattern to handle input changes

  const onSuggestionsFetchRequested = () => createEnterpriseSearchExperimentImpression();
  const onSuggestionsClearRequested = () => undefined;

  const getSuggestionValue = (hit: Hit, currentTextValue: string) => {
    if (hit.entityType === SHORT_FORM_CONTENT_VIDEO_ENTITY_TYPE) {
      return currentTextValue;
    }
    return hit.name;
  };

  const shouldRenderSuggestions = () => true;

  const getSectionSuggestions = (section: { hits: Hit[] }) => section.hits;

  const openAutosuggest = () => {
    inputRef.current?.focus();
    inputRef.current?.setAttribute('tabIndex', '0');
  };

  const autoCompleteClass = classNames(
    `rc-EnterpriseAutoComplete ${shouldRenderScopeSelect ? '--scope' : '--no-scope'} horizontal-box`,
    { isFocused }
  );

  const suggestions = [
    ...(skillsetHits.length > 0 ? skillsetHits : []),
    ...(skillHits.length > 0 && enableSkillsInSearchAndBrowse ? skillHits : []),
    ...autocompleteSuggestions,
  ];

  return (
    <AppInfoContext.Consumer>
      {({ userAgent }) => (
        <Media query={{ maxWidth: `${PAGE_HEADER_MOBILE_BREAKPOINT}px` }} defaultMatches={userAgent?.isMobile}>
          {(matches) => {
            const showMobileIconView = matches && !forceFullWidthMobile;
            return (
              <div
                id={`short-form-content-enabled-${shouldShowShortFormContent}`}
                data-e2e="EnterpriseAutoComplete"
                className={autoCompleteClass}
                {...(showMobileIconView
                  ? { css: [styles.autoComplete, isFocused && styles.autoCompleteIsFocused] }
                  : { css: [styles.preUnifiedPHSearchBar, forceFullWidthMobile && styles.forcedDesktopMode] })}
              >
                {!forceFullWidthMobile && (
                  <button
                    type="button"
                    className="nostyle mobile-magnifier"
                    aria-label={_t('Enter Search')}
                    // Tracking it programatically to pass the query data
                    onMouseDown={searchButtonOnClick}
                    css={showMobileIconView && styles.autoCompleteSearchIcon(isFocused)}
                  >
                    <SearchIcon color="invert" size="medium" />
                  </button>
                )}
                {showMobileIconView && (
                  <IconButton
                    intent="search"
                    variant="ghost"
                    size="medium"
                    onClick={openAutosuggest}
                    css={styles.mobileSearchIcon}
                    aria-label={_t('Open Search Auto Complete')}
                  />
                )}
                {!showMobileIconView ? (
                  <Imgix
                    className="coursera-logo-enterprise-search"
                    src={CourseraLogo}
                    maxWidth={32}
                    maxHeight={32}
                    alt={_t('Coursera logo')}
                    css={styles.preUnifiedPHSearchBarCourseraIcon}
                  />
                ) : null}
                <Autosuggest<SuggestionType>
                  // The first index is the placeholder index
                  id="enterpriseAutoComplete"
                  suggestions={suggestions}
                  focusInputOnSuggestionClick={false}
                  multiSection={true}
                  onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                  onSuggestionSelected={onSuggestionSelected}
                  onSuggestionsClearRequested={onSuggestionsClearRequested}
                  getSuggestionValue={(suggestion) => getSuggestionValue(suggestion, currentText)}
                  renderSuggestion={renderAutoCompleteSuggestion}
                  shouldRenderSuggestions={shouldRenderSuggestions}
                  inputProps={{
                    placeholder,
                    value: currentText,
                    onChange: onAutoSuggestChange,
                    onKeyDown,
                    onFocus,
                    onBlur: onBlur(showMobileIconView ? '-1' : undefined),
                    'aria-label': _t('Search catalog'),
                  }}
                  renderSectionTitle={renderSectionTitle}
                  getSectionSuggestions={getSectionSuggestions}
                  ref={storeInputReference}
                />
                {!showMobileIconView && (
                  <button
                    type="button"
                    className="nostyle search-button unified"
                    aria-label={_t('Enter Search')}
                    // Tracking it programatically to pass the query data
                    onClick={searchButtonOnClick}
                    ref={searchButtonRef}
                    tabIndex={0}
                  >
                    <SearchIcon color="invert" size="small" />
                  </button>
                )}
                {isFocused && (
                  <TrackedButton
                    trackingName="close_auto_complete"
                    className="nostyle cancel-button"
                    aria-label={_t('Close Search Auto Complete')}
                    onClick={clearSearchQueryAndCloseContainer}
                  >
                    <CloseIcon className="close-icon-enterprise-search-icon" size="medium" />
                  </TrackedButton>
                )}
              </div>
            );
          }}
        </Media>
      )}
    </AppInfoContext.Consumer>
  );
};

const EnterpriseAutoCompleteWrapper = ({
  pathname,
  placeholder,
  programIds,
  enableSkillsInSearchAndBrowse,
  shouldShowShortFormContent,
  shouldRenderScopeSelect,
  forceFullWidthMobile,
}: Props) => {
  const searchConfigs = useMemo(() => getEnterpriseSearchConfigs({ searchInPrograms: { programIds } }), [programIds]);
  return (
    <SearchProvider searchConfigs={searchConfigs}>
      <EnterpriseAutoComplete
        programIds={programIds}
        pathname={pathname}
        placeholder={placeholder}
        enableSkillsInSearchAndBrowse={enableSkillsInSearchAndBrowse}
        shouldShowShortFormContent={shouldShowShortFormContent}
        shouldRenderScopeSelect={shouldRenderScopeSelect}
        forceFullWidthMobile={forceFullWidthMobile}
      />
    </SearchProvider>
  );
};

export default EnterpriseAutoCompleteWrapper;
